import { useNavigate } from 'react-router';
import { Box, Center, Column, Divider, Row, Text, ZStack } from '~/commonComponents';
import { EnablePermanentJobM2M } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import HY_Tooth from '~/images/HY_tooth.png';
import { ChevronRight } from '~/images/icons';
import HeartBorderedIcon from '~/images/icons/HeartBorderedIcon';
import ShieldCheckIcon from '~/images/shield-check.svg';
import StarIcon from '~/images/star_icon.svg';
import { IJob, IUser } from '~/models';
import { Colors } from '~/themes/colors';
import { TNullable } from '~/types/Nullable';
import { CompanyMatchLabel } from '../CompanyMatchLabel/CompanyMatchLabel';

interface IProfessionalProfileProps {
  job: TNullable<IJob>;
  professional: TNullable<IUser>;
  disableNavigation?: boolean;
  hasBackgroundCheck?: boolean;
  hasWorkedWithDSO?: boolean;
  hasWorkedWithOffice?: boolean;
  onTrackOverviewClick?: () => void;
}

export const ProfessionalProfile = ({
  job,
  professional,
  disableNavigation = false,
  hasBackgroundCheck = false,
  hasWorkedWithDSO = false,
  hasWorkedWithOffice = false,
  onTrackOverviewClick,
}: IProfessionalProfileProps) => {
  const navigate = useNavigate();

  const isEnablePermanentJobM2M = useFeatureFlag(EnablePermanentJobM2M);

  const isFavorite = professional?.isFavorite;
  const hasProfileImage = !!professional?.profile_image;

  const handleNavigation = () => {
    if (!job || !professional) return;

    onTrackOverviewClick?.();

    if (isEnablePermanentJobM2M && job.isOnsiteInterview) {
      navigate(`/jobs/${job.id}/candidates/${professional.id}/overview`);
    } else {
      navigate(`/dashboard/candidate/${job.id}`, {
        state: { candidateId: professional?.id, state: job?.state, profession: job?.profession },
      });
    }
  };

  return (
    <Row alignItems="center" gap={20}>
      <Center
        borderColor={isFavorite ? Colors.favorites_400 : 'transparent'}
        borderRadius="100%"
        borderStyle="solid"
        borderWidth={3}
        height={104}
        padding={2}
        position="relative"
        width={104}
      >
        {hasProfileImage ? (
          <img
            src={professional.profile_image}
            style={{
              borderRadius: 104,
              height: 104,
              objectFit: 'cover',
              width: 104,
            }}
            alt="profile_image"
          />
        ) : (
          <Box className="default_circle_container">
            <img src={HY_Tooth} alt="profile_image" width="104px" />
          </Box>
        )}
        {isFavorite && (
          <ZStack right={-4} top="72%">
            <HeartBorderedIcon />
          </ZStack>
        )}
      </Center>

      <Column marginBottom={20}>
        <Row alignItems="center" flex={1} gap={8} marginTop={15}>
          <Text
            bold
            fontSize={22}
            maxWidth={280}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {professional?.full_name}
          </Text>

          <Row alignItems="center" gap={4}>
            <img
              src={StarIcon}
              alt="star_icon"
              style={{
                height: 24,
                width: 24,
              }}
            />
            <Text bold fontSize={20}>
              {professional?.average_rating?.toFixed(1) || '5.0'}
            </Text>
          </Row>

          {!disableNavigation && (
            <>
              <Divider color={Colors.neutral_600} height={24} marginVertical={0} width={1.5} />
              <Row alignItems="center" cursor="pointer" gap={8} onClick={handleNavigation}>
                <Text bold color={Colors.secondary_500} fontSize={20}>
                  {isEnablePermanentJobM2M && job?.isOnsiteInterview ? 'Overview' : 'Profile'}
                </Text>
                <ChevronRight size={24} />
              </Row>
            </>
          )}
        </Row>
        <Text>Drivers License Verified</Text>
        <Row alignItems="center" gap={4} marginBottom={8}>
          <Text>
            {professional?.license?.subProfession ?? professional?.license?.profession} Credentials
            Verified
          </Text>
          {hasBackgroundCheck && (
            <>
              <Divider color={Colors.neutral_600} height={16} marginVertical={0} width={1.5} />
              <Row alignItems="center" gap={4}>
                <Text>Background Checked</Text>
                <img
                  src={ShieldCheckIcon}
                  alt="shield-check"
                  style={{
                    height: 24,
                    margin: 0,
                    width: 24,
                  }}
                />
              </Row>
            </>
          )}
        </Row>
        <CompanyMatchLabel
          offerOwnerId={job?.offer_owner.id || ''}
          professional={professional}
          hasWorkedWithDSO={hasWorkedWithDSO}
          hasWorkedWithOffice={hasWorkedWithOffice}
        />
      </Column>
    </Row>
  );
};
